import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useLocaleState, useNotify } from 'react-admin'
import { checkInUser, getGroup } from '@api'
import { AppContext } from '@/context/AppContext'
import { PERMISSION_DEFAULTS, PermissionITF } from '@/authProvider'
import { usePermissions } from '@hooks/useHasPermission'
import { selectedGroup, selectedProperty, setUserData } from '@reducer/profile'
import { setPermissions, setPermissionVersion } from '@store/reducers/auth'
import { UserITF } from '@/types'

/** The query parameter the portfolio screens mirror their building pill into (DESIGN_GUIDE.md 7.12). */
export const PORTFOLIO_BUILDINGS_PARAM = 'bIds'

/** /p/:gId/<section>: the portfolio-scoped path, or the bare section when no portfolio is selected (the shims land it). */
export const portfolioPath = (gId: string | undefined | null, section: string) => (gId ? `/p/${gId}${section}` : section)

// Memberships the backend builds no permissions for (resolveContext): a link
// into such a group would open an empty, fully denied session.
const DEAD_MEMBERSHIP = new Set(['Archived', 'Offboarded'])

/**
 * Whether the signed-in user may open a portfolio from a link, decided from
 * what the session already knows. AcAdmin (GlobalManageCustomers) may open
 * any; everyone else needs a live membership in the group. The backend keeps
 * its own guards on every group-scoped read, so this check only decides
 * whether to switch the session: it never grants anything by itself.
 */
export const canOpenPortfolio = (user: UserITF | null | undefined, globalManageCustomers: boolean, groupId: string) => {
  if (!groupId) return false
  if (globalManageCustomers || user?.IsAcAdmin) return true
  const memberships = user?.Memberships
  if (!Array.isArray(memberships)) return false
  return memberships.some((m) => m?.GroupId === groupId && !DEAD_MEMBERSHIP.has(String(m.MembershipStatus || '')))
}

/** The same page on another portfolio: /p/<old>/... -> /p/<gId>/..., minus the building ids, which belonged to the old one. */
export const pathOnPortfolio = (pathname: string, search: string, gId: string) => {
  const next = new URLSearchParams(search)
  next.delete(PORTFOLIO_BUILDINGS_PARAM)
  const query = next.toString()
  return `${pathname.replace(/^\/p\/[^/]+/, `/p/${gId}`)}${query ? `?${query}` : ''}`
}

/**
 * Keeps the selected portfolio in step with the /p/:gId segment of the URL,
 * the way BuildingScope keeps the building in step with /b/:bId. The URL is
 * the source of truth: whenever it names a portfolio other than the selected
 * one, that portfolio is opened the same way the app bar switcher opens it.
 * For a member of several portfolios the session is re-pointed server-side
 * (User/CheckIn) so permissions and feature flags follow; for AcAdmin the
 * group is looked up and selected. A portfolio the user cannot open (no live
 * membership, a dead id, a backend that resolved a different group) yields a
 * redirect to the same page on the portfolio already selected, with the
 * link's building ids dropped, and nothing is fetched for it.
 *
 * Returns `switching` while a switch is in flight (PortfolioScope holds the
 * page back) and `redirectTo` when the link cannot be followed.
 */
const usePortfolioUrlScope = (gId: string) => {
  const { pathname, search } = useLocation()
  const dispatch = useDispatch()
  const notify = useNotify()
  const [locale] = useLocaleState()
  const { updateGroup } = useContext(AppContext)
  const { GlobalManageCustomers } = usePermissions()
  const { user, selectedGroupId } = useSelector((state: any) => state.profile) as { user: UserITF | null; selectedGroupId: string }
  const userLoaded = !!user
  const inSync = !gId || gId === selectedGroupId
  // The switch in flight, by portfolio: a re-render while it runs must not
  // start it twice, and an older one that answers late is ignored.
  const inFlightRef = useRef<string | null>(null)
  const [switching, setSwitching] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  useEffect(() => {
    // Session recovery has not filled the profile yet: nothing is known about
    // who may open what, so wait (userLoaded re-runs this).
    if (!userLoaded || inSync) return
    if (inFlightRef.current === gId || refused === gId) return
    const refuse = () => {
      inFlightRef.current = null
      setSwitching(false)
      setRefused(gId)
      notify('That portfolio is not available to you. Showing your current portfolio instead.', { type: 'warning' })
    }
    if (!canOpenPortfolio(user, !!GlobalManageCustomers, gId)) {
      refuse()
      return
    }
    const adopt = (groupId: string, groupName: Record<string, string> | undefined, logo?: string) => {
      const name = groupName?.[locale] || groupName?.en || ''
      dispatch(selectedGroup({ groupId, groupName: name, group: { acId: groupId, GroupName: groupName, LogoFilename: logo } }))
      updateGroup({ currentGroupId: groupId, currentGroupName: name })
      // A building belongs to one portfolio: the selection cannot survive the switch.
      dispatch(selectedProperty({ propertyId: '', propertyName: '', buildingCode: '', propertyTag: {} }))
    }
    inFlightRef.current = gId
    setSwitching(true)
    const isLatest = () => inFlightRef.current === gId
    const run =
      GlobalManageCustomers || user?.IsAcAdmin
        ? // AcAdmin: the group is looked up (Group/GetGroup allows cross-group for
          // GlobalManageCustomers) and selected, exactly as the app bar switcher does.
          getGroup<any>({ gId }).then((res) => {
            const group = res?.Group || res
            if (!isLatest()) return
            if (!group?.acId) {
              refuse()
              return
            }
            adopt(group.acId, group.GroupName, group.LogoFilename)
          })
        : // Member of several portfolios: re-point the session so permissions and
          // feature flags follow. The backend resolves a group it will not open to
          // a fallback; adopting anything but the asked-for group would silently show
          // another portfolio under this link, so that counts as a refusal.
          checkInUser<UserITF>({ GroupId: gId }).then((res) => {
            const ctx = res?.UserContext
            if (!isLatest()) return
            if (!ctx?.CurrentGroupId || ctx.CurrentGroupId !== gId) {
              refuse()
              return
            }
            // Same adoption as usePermissionRefresh: the context (permissions,
            // feature flags, building access) and the membership list follow.
            dispatch(setUserData({ ...user, UserContext: ctx, Memberships: res.Memberships ?? user?.Memberships }))
            const permissions: PermissionITF = {
              ...PERMISSION_DEFAULTS,
              ...(ctx.PermissionsHash || {}),
              PortfolioTeam: !!res.PortfolioTeam,
            }
            dispatch(setPermissions(permissions))
            if (typeof ctx.PermissionVersion === 'number') dispatch(setPermissionVersion(ctx.PermissionVersion))
            adopt(ctx.CurrentGroupId, ctx.CurrentGroupName as Record<string, string> | undefined, ctx.CurrentGroupLogoFilename)
          })
    run
      .catch(() => {
        if (isLatest()) refuse()
      })
      .finally(() => {
        if (isLatest()) {
          inFlightRef.current = null
          setSwitching(false)
        }
      })
    // notify, locale and updateGroup are stable for the life of the page; user
    // and GlobalManageCustomers are read when a switch runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gId, inSync, userLoaded, refused, dispatch])

  // The refusal is for one URL: a later link into the same portfolio (after a
  // membership was added, say) is tried again.
  useEffect(() => {
    if (inSync && refused) setRefused(null)
  }, [inSync, refused])

  // Where a refused link lands: the same page on the selected portfolio, or
  // Home when none is selected (a global admin who has not picked one yet).
  const redirectTo = !inSync && refused === gId ? (selectedGroupId ? pathOnPortfolio(pathname, search, selectedGroupId) : '/') : null
  return { switching: !inSync && switching, redirectTo }
}

export default usePortfolioUrlScope
